import { ExternalLink, Sparkles, Bot, Mic, Globe, ArrowRight, Presentation, Code2 } from 'lucide-react'
import { Link } from 'react-router-dom'

const projects = [
  {
    title: 'Easy Resume',
    description:
      'AI-powered Firefox extension that tailors resumes and cover letters based on job descriptions. Uses NVIDIA NIM API for ATS analysis and intelligent customization. Features multiple templates, PDF export, profile management, and history tracking.',
    tags: ['React', 'TypeScript', 'Vite', 'NVIDIA NIM', 'Web Extension'],
    icon: <Globe size={24} />,
    links: { github: 'https://github.com/Viresh26', live: undefined },
    detailPath: '/projects/easy-resume',
  },
  {
    title: 'Kitchen Rota',
    description:
      'Full-stack web application for managing shared kitchen cleaning responsibilities. Features user authentication, task scheduling with rotation, completion tracking, and an admin panel. Built with Next.js 15, Prisma, and deployed on Cloudflare Pages.',
    tags: ['Next.js 15', 'React 19', 'TypeScript', 'Prisma', 'Cloudflare'],
    icon: <Sparkles size={24} />,
    links: { github: 'https://github.com/Viresh26', live: undefined },
    detailPath: '/projects/kitchen-rota',
  },
  {
    title: 'Job Application Agent',
    description:
      'Dual-agent automated job application system. The "Architect" agent handles semantic matching and LaTeX resume synthesis, while the "Navigator" agent performs vision-based web interaction and form traversal. Uses OpenRouter API with multiple specialized LLMs.',
    tags: ['Python', 'LLM Orchestration', 'Playwright', 'Docker', 'LaTeX'],
    icon: <Bot size={24} />,
    links: { github: undefined, live: undefined },
    detailPath: '/projects/job-application-agent',
  },
  {
    title: 'Fitness Trainer',
    description:
      'Native Android app reading health data from Google Health Connect with an AI fitness coach. Kotlin + Jetpack Compose + Hilt. Three-tier AI fallback (OpenRouter → NVIDIA → Gemini) with real-time streaming responses.',
    tags: ['Kotlin', 'Jetpack Compose', 'Health Connect', 'Hilt', 'AI'],
    icon: <Sparkles size={24} />,
    links: { github: undefined, live: undefined },
    detailPath: '/projects/fitness-trainer',
  },
  {
    title: 'Audio Synthesis Research',
    description:
      "Master's research on robustness testing of speech synthesis models. Built automated testing pipelines to identify and handle errors in production-like scenarios. Worked with CosyVoice 3 Instruct, Qwen3 TTS, Qwen3 Omni, ParlerTTS, and NISQA.",
    tags: ['Speech Synthesis', 'Python', 'CosyVoice', 'Qwen3', 'NISQA'],
    icon: <Mic size={24} />,
    links: { github: undefined, live: undefined },
    detailPath: '/projects/audio-synthesis',
  },
  {
    title: 'PPT Maker (Simon-Kucher)',
    description:
      'Internal automation tool that transforms Excel/JSON data into professional PowerPoint slides with native graphs and AI-generated analysis. Built with Python and OpenAI API, reducing manual slide creation effort by 80%.',
    tags: ['Python', 'OpenAI API', 'Prompt Engineering', 'Automation'],
    icon: <Presentation size={24} />,
    links: { github: undefined, live: undefined },
    detailPath: '/projects/ppt-maker',
  },
]

export default function Projects() {
  return (
    <section id="projects" className="section-padding bg-surface-light/30">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-headline">
          Featured <span className="text-gradient">Projects</span>
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Link
              key={project.title}
              to={project.detailPath || '#'}
              className="block group"
            >
              <div className="bg-surface-container-low rounded-xl border border-outline-variant/10 p-6 card-hover flex flex-col h-full cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-primary">{project.icon}</div>
                  <div className="flex gap-3">
                    {project.links.github && (
                      <a
                        href={project.links.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-500 hover:text-white transition-colors"
                        aria-label="GitHub"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Code2 size={18} />
                      </a>
                    )}
                    {project.links.live && (
                      <a
                        href={project.links.live}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-500 hover:text-white transition-colors"
                        aria-label="Live demo"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                    {project.detailPath && (
                      <div className="text-gray-500 group-hover:text-primary transition-colors">
                        <ArrowRight size={18} />
                      </div>
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-white mb-2 font-headline group-hover:text-primary transition-colors">
                  {project.title}
                </h3>
                <p className="text-on-surface-variant text-sm leading-relaxed flex-1 mb-4">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
